import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import {
  getMyPortfolio,
  createPortfolio,
  updatePortfolio,
  clearState,
  deletePortfolio,
} from "../../../Redux/Portfolio/slice";
import {
  Portfolio,
  CreatePortfolioRequest,
  UpdatePortfolioRequest,
  LanguageEnum,
  LocationPreference,
  EducationLevel,
} from "../../../Redux/Portfolio/types";

export interface PortfolioFormData {
  aboutMe: string;
  height: string;
  weight: string;
  bust: string;
  waist: string;
  hips: string;
  chest: string;
  biceps: string;
  hairType: string;
  eyeColor: string;
  skills: string;
  knownLanguages: LanguageEnum[];
  fluentLanguages: LanguageEnum[];
  locationPreference: LocationPreference[];
  educationLevel: EducationLevel | undefined;
  degree: string;
  contactName: string;
  email: string;
  mobile: string;
  facebook: string;
  instagram: string;
  twitter: string;
  youtube: string;
  name: string;
  gender: string;
  mainImage: string;
  professions: string[];
  photos: string[];
  instagramReels: string[];
  youtubeVideos: string[];
  // Experience fields
  movieNames: string;
  webseriesNames: string;
  tvShowNames: string;
  shortFilmNames: string;
  adNames: string;
  fashionEventNames: string;
}

interface PortfolioContextType {
  myPortfolio: Portfolio | null;
  loading: boolean;
  error: string | null;
  success: boolean;
  message: string | null;
  formData: PortfolioFormData;
  setFormData: React.Dispatch<React.SetStateAction<PortfolioFormData>>;
  handleGlobalSave: () => void;
  fetchMyPortfolio: () => void;
  createNewPortfolio: (data: CreatePortfolioRequest) => void;
  updateExistingPortfolio: (data: UpdatePortfolioRequest) => void;
  removePortfolio: () => void;
  clearPortfolioState: () => void;
}

const PortfolioContext = createContext<PortfolioContextType | undefined>(
  undefined,
);

export const PortfolioProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { myPortfolio, loading, error, success, message } = useSelector(
    (state: RootState) => state.portfolio,
  );

  const [formData, setFormData] = useState<PortfolioFormData>({
    aboutMe: "",
    height: "",
    weight: "",
    bust: "",
    waist: "",
    hips: "",
    chest: "",
    biceps: "",
    hairType: "",
    eyeColor: "",
    skills: "",
    knownLanguages: [],
    fluentLanguages: [],
    locationPreference: [],
    educationLevel: undefined,
    degree: "",
    contactName: "",
    email: "",
    mobile: "",
    facebook: "",
    instagram: "",
    twitter: "",
    youtube: "",
    name: "",
    gender: "",
    mainImage: "",
    professions: [],
    photos: [],
    instagramReels: [],
    youtubeVideos: [],
    // Experience fields
    movieNames: "",
    webseriesNames: "",
    tvShowNames: "",
    shortFilmNames: "",
    adNames: "",
    fashionEventNames: "",
  });

  const initialized = React.useRef(false);

  useEffect(() => {
    if (myPortfolio && !initialized.current) {
      setFormData((prev) => ({
        ...prev,
        aboutMe: myPortfolio.aboutMe || "",
        height: myPortfolio.height?.toString() || "",
        weight: myPortfolio.weight?.toString() || "",
        bust: myPortfolio.bust?.toString() || "",
        waist: myPortfolio.waist?.toString() || "",
        hips: myPortfolio.hips?.toString() || "",
        chest: myPortfolio.chest?.toString() || "",
        biceps: myPortfolio.biceps?.toString() || "",
        hairType: myPortfolio.hairType || "",
        eyeColor: myPortfolio.eyeColor || "",
        skills: myPortfolio.skills || "",
        knownLanguages: myPortfolio.knownLanguages || [],
        fluentLanguages: myPortfolio.fluentLanguages || [],
        locationPreference: myPortfolio.locationPreference || [],
        educationLevel: myPortfolio.educationLevel,
        degree: myPortfolio.degree || "",
        contactName: myPortfolio.contactName || "",
        email: myPortfolio.email || "",
        mobile: myPortfolio.mobile || "",
        facebook: myPortfolio.facebook || "",
        instagram: myPortfolio.instagram || "",
        twitter: myPortfolio.twitter || "",
        youtube: myPortfolio.youtube || "",
        name: myPortfolio.name || "",
        gender: myPortfolio.gender || "",
        mainImage: myPortfolio.mainImage || "",
        professions: myPortfolio.professions || [],
        photos: myPortfolio.photos || [],
        instagramReels: myPortfolio.instagramReels || [],
        youtubeVideos: myPortfolio.youtubeVideos || [],
        movieNames: myPortfolio.movieNames?.join(", ") || "",
        webseriesNames: myPortfolio.webseriesNames?.join(", ") || "",
        tvShowNames: myPortfolio.tvShowNames?.join(", ") || "",
        shortFilmNames: myPortfolio.shortFilmNames?.join(", ") || "",
        adNames: myPortfolio.adNames?.join(", ") || "",
        fashionEventNames: myPortfolio.fashionEventNames?.join(", ") || "",
      }));
      initialized.current = true;
    }
  }, [myPortfolio]);

  const handleGlobalSave = () => {
    // Shared formatting for numeric fields
    const parsedData = {
      aboutMe: formData.aboutMe,
      hairType: formData.hairType,
      eyeColor: formData.eyeColor,
      skills: formData.skills,
      knownLanguages: formData.knownLanguages,
      fluentLanguages: formData.fluentLanguages,
      locationPreference: formData.locationPreference,
      educationLevel: formData.educationLevel,
      degree: formData.degree,
      contactName: formData.contactName,
      email: formData.email,
      mobile: formData.mobile,
      facebook: formData.facebook,
      instagram: formData.instagram,
      twitter: formData.twitter,
      youtube: formData.youtube,
      name: formData.name || "User",
      gender: formData.gender || "Male",
      mainImage:
        formData.mainImage ||
        "https://res.cloudinary.com/dummy/image/upload/v1/placeholder.jpg",
      professions:
        formData.professions.length > 0 ? formData.professions : ["Actor"],
      photos: formData.photos,
      instagramReels: formData.instagramReels,
      youtubeVideos: formData.youtubeVideos,
      height: formData.height ? parseInt(formData.height) : undefined,
      weight: formData.weight ? parseInt(formData.weight) : undefined,
      bust: formData.bust ? parseInt(formData.bust) : undefined,
      waist: formData.waist ? parseInt(formData.waist) : undefined,
      hips: formData.hips ? parseInt(formData.hips) : undefined,
      chest: formData.chest ? parseInt(formData.chest) : undefined,
      biceps: formData.biceps ? parseInt(formData.biceps) : undefined,
      // Shared formatting for string array fields (experience)
      movieNames: formData.movieNames
        ? formData.movieNames.split(",").map((s) => s.trim())
        : [],
      webseriesNames: formData.webseriesNames
        ? formData.webseriesNames.split(",").map((s) => s.trim())
        : [],
      tvShowNames: formData.tvShowNames
        ? formData.tvShowNames.split(",").map((s) => s.trim())
        : [],
      shortFilmNames: formData.shortFilmNames
        ? formData.shortFilmNames.split(",").map((s) => s.trim())
        : [],
      adNames: formData.adNames
        ? formData.adNames.split(",").map((s) => s.trim())
        : [],
      fashionEventNames: formData.fashionEventNames
        ? formData.fashionEventNames.split(",").map((s) => s.trim())
        : [],
      // Update boolean flags based on presence of names
      hasMovies: formData.movieNames
        .split(",")
        .some((s) => s.trim().length > 0),
      hasWebseries: formData.webseriesNames
        .split(",")
        .some((s) => s.trim().length > 0),
      hasTVShows: formData.tvShowNames
        .split(",")
        .some((s) => s.trim().length > 0),
      hasShortFilms: formData.shortFilmNames
        .split(",")
        .some((s) => s.trim().length > 0),
      hasAds: formData.adNames.split(",").some((s) => s.trim().length > 0),
      hasModel: formData.fashionEventNames
        .split(",")
        .some((s) => s.trim().length > 0),
    };

    if (myPortfolio) {
      dispatch(updatePortfolio(parsedData as any));
    } else {
      dispatch(createPortfolio(parsedData as any));
    }
  };

  const fetchMyPortfolio = () => {
    dispatch(getMyPortfolio());
  };

  const createNewPortfolio = (data: CreatePortfolioRequest) => {
    dispatch(createPortfolio(data as any));
  };

  const updateExistingPortfolio = (data: UpdatePortfolioRequest) => {
    dispatch(updatePortfolio(data));
  };

  const removePortfolio = () => {
    dispatch(deletePortfolio());
  };

  const clearPortfolioState = () => {
    dispatch(clearState());
  };

  useEffect(() => {
    fetchMyPortfolio();
    return () => {
      clearPortfolioState();
    };
  }, [dispatch]);

  const value: PortfolioContextType = {
    myPortfolio,
    loading,
    error,
    success,
    message,
    formData,
    setFormData,
    handleGlobalSave,
    fetchMyPortfolio,
    createNewPortfolio,
    updateExistingPortfolio,
    removePortfolio,
    clearPortfolioState,
  };

  return (
    <PortfolioContext.Provider value={value}>
      {children}
    </PortfolioContext.Provider>
  );
};

export const usePortfolioContext = (): PortfolioContextType => {
  const context = useContext(PortfolioContext);
  if (context === undefined) {
    throw new Error(
      "usePortfolioContext must be used within a PortfolioProvider",
    );
  }
  return context;
};
